import { useCallback, useEffect, useRef, useState } from 'react';
import { BARRAS, barrasDeIdentificador, barrasDeMuestras } from '../../lib/onda-de-audio.ts';

/**
 * Una nota de voz, con su onda.
 *
 * El `<audio controls>` nativo no sirve acá por dos razones: colapsa los mandos
 * cuando la burbuja es angosta —ya pasó— y no dice nada sobre el contenido. En
 * una bandeja con veinte conversaciones, poder ver de un vistazo si un audio son
 * tres palabras o dos minutos con silencios cambia cómo se prioriza.
 *
 * La onda se intenta **real** decodificando el audio en el navegador de quien
 * mira. Si el navegador no puede con el formato —Safari falla con Opus dentro de
 * Ogg, que es justo lo que manda WhatsApp— cae a barras derivadas del
 * identificador: se ven parecidas y nadie queda con un reproductor roto.
 */

function mmss(segundos: number): string {
  if (!Number.isFinite(segundos) || segundos < 0) return '0:00';
  const m = Math.floor(segundos / 60);
  const s = Math.floor(segundos % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function NotaDeVoz({ url, id }: { url: string; id: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [sonando, setSonando] = useState(false);
  const [posicion, setPosicion] = useState(0);
  const [duracion, setDuracion] = useState(0);
  // Arranca con el respaldo para que la onda esté desde el primer cuadro:
  // esperar a la decodificación dejaría un hueco que se lee como algo roto.
  const [barras, setBarras] = useState<number[]>(() => barrasDeIdentificador(id));

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const res = await fetch(url);
        const buffer = await res.arrayBuffer();
        const Ctx =
          window.AudioContext ??
          (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const decodificado = await ctx.decodeAudioData(buffer);
        const reales = barrasDeMuestras(decodificado.getChannelData(0), BARRAS);
        void ctx.close();
        if (vivo && reales.length > 0) setBarras(reales);
      } catch {
        // El navegador no supo con este formato. El respaldo ya está dibujado y
        // el audio se reproduce igual — el `<audio>` sí lo soporta aunque la Web
        // Audio API no pueda decodificarlo.
      }
    })();
    return () => {
      vivo = false;
    };
  }, [url]);

  const alternar = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play();
    else a.pause();
  }, []);

  /** Clic sobre la onda para saltar: lo más usado cuando el audio es largo. */
  const saltarA = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const a = audioRef.current;
    if (!a || !Number.isFinite(a.duration)) return;
    const caja = e.currentTarget.getBoundingClientRect();
    const proporcion = Math.min(1, Math.max(0, (e.clientX - caja.left) / caja.width));
    a.currentTime = proporcion * a.duration;
    setPosicion(a.currentTime);
  }, []);

  const avance = duracion > 0 ? posicion / duracion : 0;

  /**
   * Las barras con clave propia.
   *
   * La identidad de una barra ES su posición: la número 7 es la 7 y nunca se
   * reordena ni se inserta. Se arma acá para que la clave sea un valor y no el
   * índice del `map` — una clave por VALOR sería peor, porque los valores
   * cambian cuando termina de decodificarse el audio real y React remontaría las
   * cuarenta barras en vez de actualizarlas.
   */
  const conClave = barras.map((alto, i) => ({
    alto,
    clave: `${id}-b${i}`,
    posicion: i / barras.length,
  }));

  return (
    /* Todo el reproductor se dibuja con `currentColor`, heredando el color de
       texto de su burbuja. Es lo que lo hace correcto en los cuatro fondos donde
       aparece: la burbuja de la persona es gris claro en tema claro y gris
       oscuro en tema oscuro, y la del bot es un degradado con texto blanco. Un
       blanco fijo —que fue mi primera versión— era invisible sobre el gris
       claro, el mismo error que ya cometí con el tilde. */
    <div className="mt-1 flex items-center gap-2 rounded-full bg-current/10 px-2.5 py-1.5">
      <button
        type="button"
        onClick={alternar}
        aria-label={sonando ? 'Pausar la nota de voz' : 'Reproducir la nota de voz'}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-current/40 bg-current/10 text-current transition hover:bg-current/20"
      >
        <span aria-hidden className="text-sm leading-none">
          {sonando ? '❚❚' : '▶'}
        </span>
      </button>

      {/* biome-ignore lint/a11y/useKeyWithClickEvents: los mandos accesibles son
          el botón y el <audio>; la onda es un atajo de puntero y va marcada
          aria-hidden para no duplicar controles en el lector de pantalla. */}
      <div
        aria-hidden
        onClick={saltarA}
        className="flex h-8 min-w-[140px] flex-1 cursor-pointer items-center gap-[2px]"
      >
        {conClave.map(({ alto, clave, posicion: p }) => {
          const reproducida = p <= avance;
          return (
            <span
              key={clave}
              className={`w-[2px] flex-1 rounded-full bg-current ${reproducida ? '' : 'opacity-30'}`}
              style={{ height: `${Math.max(12, alto * 100)}%` }}
            />
          );
        })}
      </div>

      <span className="shrink-0 text-[11px] tabular-nums opacity-80">
        {mmss(sonando || posicion > 0 ? posicion : duracion)}
      </span>

      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onPlay={() => setSonando(true)}
        onPause={() => setSonando(false)}
        onEnded={() => {
          setSonando(false);
          setPosicion(0);
        }}
        onTimeUpdate={(e) => setPosicion(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuracion(e.currentTarget.duration)}
        className="hidden"
      >
        <track kind="captions" />
      </audio>
    </div>
  );
}
